import memoizeOne from 'memoize-one';
import React, { Component, Fragment, ReactNode } from 'react';
import { Product, ReferencePrice } from '~/Api/User/ProductGroup/CzMaxPrice';
import ComplianceIcon from '~/CzSuklComplianceIcon';
import { DebounceInput } from '~/Debounce';
import parseHumanFloat from '~/libs/Number/parseHumanFloat';
import { calculateProductPackagePrice } from '~/Product';
import { Link } from '~/UI/Button';
import { Product as GeneralProduct } from '~/Api/Product';
import Checkbox from '~/UI/Checkbox/Checkbox';
import CountryIcon from '~/UI/CountryIcon/CountryIcon';
import Icon from '~/UI/Icon/Icon';
import inputCompactStyles from '~/UI/Input/compact.less';
import Input from '~/UI/Input/Input';
import defaultSegmentedSelectStyles from '~/UI/SegmentedSelect/default.less';
import SegmentedSelectOption from '~/UI/SegmentedSelect/Option/SegmentedSelectOption';
import SegmentedSelectPage from '~/UI/SegmentedSelect/Page/SegmentedSelectPage';
import SegmentedSelectSegment from '~/UI/SegmentedSelect/Segment/SegmentedSelectSegment';
import SegmentedSelect from '~/UI/SegmentedSelect/SegmentedSelect';
import Tooltip from '~/UI/Tooltip';

import styles from './styles.less';

export interface ReferencePriceValue {
	value: string;
	parentValue: null;
	prompt: React.ReactNode;
}

export interface Values {
	referencePrice: {
		value: ReferencePriceValue | null;
		isValid: boolean;
	};
	packageSizeForSingleUnit: {
		value: string;
		isValid: boolean | null;
	};
	packageSize: {
		value: string;
		isValid: boolean | null;
	};
	selected: boolean;
}
export type OnChangeListener = (
	product: Product,
	referencePrice: ReferencePriceValue | null,
	packageSize: string,
	packageSizeForSingleUnit: string
) => void;

export type OnSelectListener = (product: Product, selected: boolean, range: boolean) => void;

interface Props {
	czMaxPriceProduct: Product;
	values: Values;
	referencePackageSize: number | null;
	disableSelection: boolean;
	highlightProduct: boolean;
	lastSelectedProduct: boolean;
	detailLink(product: { id: string }): { pathname: string; search: string };
	style: any;
	lowestPriceIndex: number | null;
	onChange: OnChangeListener;
	onSelect: OnSelectListener;
	onSave(): void;
	checkRow(value: boolean): void;
	checked: boolean;
}

export default class Row extends Component<Props> {
	static defaultProps = {
		referencePackageSize: null,
		disableSelection: false,
		highlightProduct: false,
		lastSelectedProduct: false,
		style: null,
	};

	resolveReferencePrice = memoizeOne((type: string | null, czMaxPriceProduct: Product): ReferencePrice | null => {
		if (type === null) {
			return null;
		}
		return (
			czMaxPriceProduct.referencePrices.find((referencePrice) => referencePrice.calculatedPrice.type === type) ?? null
		);
	});

	shouldComponentUpdate(nextProps: Readonly<Props>) {
		return (
			JSON.stringify(this.props.czMaxPriceProduct) !== JSON.stringify(nextProps.czMaxPriceProduct) ||
			this.props.values.packageSize.isValid !== nextProps.values.packageSize.isValid ||
			this.props.values.packageSize.value !== nextProps.values.packageSize.value ||
			this.props.values.packageSizeForSingleUnit.isValid !== nextProps.values.packageSizeForSingleUnit.isValid ||
			this.props.values.packageSizeForSingleUnit.value !== nextProps.values.packageSizeForSingleUnit.value ||
			this.props.values.referencePrice.isValid !== nextProps.values.referencePrice.isValid ||
			this.props.values.referencePrice.value?.value !== nextProps.values.referencePrice.value?.value ||
			this.props.values.selected !== nextProps.values.selected ||
			this.props.referencePackageSize !== nextProps.referencePackageSize ||
			this.props.disableSelection !== nextProps.disableSelection ||
			this.props.highlightProduct !== nextProps.highlightProduct ||
			this.props.lastSelectedProduct !== nextProps.lastSelectedProduct ||
			this.props.style !== nextProps.style ||
			this.props.lowestPriceIndex !== nextProps.lowestPriceIndex ||
			this.props.onChange !== nextProps.onChange ||
			this.props.onSelect !== nextProps.onSelect ||
			this.props.checked !== nextProps.checked
		);
	}

	render() {
		const {
			czMaxPriceProduct,
			values,
			referencePackageSize,
			disableSelection,
			highlightProduct,
			lastSelectedProduct,
			detailLink,
			style,
			lowestPriceIndex,
			onChange,
			onSave,
			checked,
			checkRow,
		} = this.props;
		const { product } = czMaxPriceProduct;
		const detailLinkString = detailLink(product);
		const referencePrice = this.resolveReferencePrice(values.referencePrice.value?.value ?? null, czMaxPriceProduct);
		const prompt = referencePrice !== null ? this.renderReferencePricePrompt(referencePrice) : null;

		return (
			<div
				className={
					product.status.isRemoved() || product.status.isBeforeIntroduction()
						? styles.removedRow
						: highlightProduct
						? styles.highlightedRow
						: styles.row
				}
				data-testid='row'
				style={style}
			>
				<div className={styles.checkboxContainer} data-testid='delete-product'>
					<Checkbox checked={checked} onChange={checkRow} />
				</div>
				<div className={styles.countryCell}>
					<Link styles={{ button: styles.countryCellContent }} tabIndex={-1} to={detailLinkString}>
						<div className={styles.countryIcon}>
							<CountryIcon code={product.dataset.source.country.alpha2} name={product.dataset.source.country.name} />
						</div>
						{product.dataset.source.country.alpha2}
						<abbr className={styles.datasetSourceShortName} title={product.dataset.source.description ?? undefined}>
							{product.dataset.source.shortName}
						</abbr>
					</Link>
				</div>
				<div className={styles.brandCell}>
					<Link styles={{ button: styles.brandCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedBrandName}
					</Link>
				</div>
				<div className={styles.mahCell}>
					<Link styles={{ button: styles.mahCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedMarketingAuthorizationHolder}
					</Link>
				</div>
				<div className={styles.dosageCell}>
					<Link styles={{ button: styles.dosageCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedDosageForm}
					</Link>
				</div>
				<div className={styles.dosageStrengthCell}>
					<Link styles={{ button: styles.dosageStrengthCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.dosageStrength}
					</Link>
				</div>
				<div className={styles.packageCell}>
					{czMaxPriceProduct.singleUnitPackageSize ? (
						<div className={styles.packageInput}>
							<DebounceInput
								value={values.packageSizeForSingleUnit.value}
								onBlur={onSave}
								onChange={(value) =>
									onChange(czMaxPriceProduct, values.referencePrice.value, values.packageSize.value, value)
								}
							>
								<Input
									id={`product-package-${product.id}`}
									isInvalid={values.packageSizeForSingleUnit.isValid === false}
									styles={inputCompactStyles}
								/>
							</DebounceInput>
						</div>
					) : (
						<Link styles={{ button: styles.packageCellContent }} tabIndex={-1} to={detailLinkString}>
							{product.packageSize}
						</Link>
					)}
				</div>
				<div className={styles.referencePriceCell}>
					{czMaxPriceProduct.referencePrices.length > 1 ? (
						<Fragment>
							{this.renderReferencePriceSelect(prompt)}
							{czMaxPriceProduct.singleUnitPackageSize ? (
								<Tooltip
									position='right'
									reference={
										<span className={styles.exclamationMarkIcon}>
											<Icon name='red-exclamation-mark'>!</Icon>
										</span>
									}
									width={180}
								>
									Single unit package size
								</Tooltip>
							) : null}
						</Fragment>
					) : (
						<div className={styles.priceCellContent}>{prompt}</div>
					)}
				</div>
				<div className={styles.packageForCalculationCell}>
					<div className={styles.packageInput}>
						<DebounceInput
							value={values.packageSize.value}
							onBlur={onSave}
							onChange={(value) =>
								onChange(czMaxPriceProduct, values.referencePrice.value, value, values.packageSizeForSingleUnit.value)
							}
						>
							<Input
								id={`product-package-for-calc-${product.id}`}
								isInvalid={values.packageSize.isValid === false}
								placeholder={`${referencePackageSize ?? ''}`}
								styles={inputCompactStyles}
							/>
						</DebounceInput>
					</div>
				</div>
				<div className={styles.priceCell}>
					<Link
						data-testid='calculated-price'
						styles={{ button: styles.priceCellContent }}
						tabIndex={-1}
						to={detailLinkString}
					>
						{this.renderPackagePrice(referencePrice)}
					</Link>
				</div>
				<div className={styles.selectionCell}>
					<ComplianceIcon compliance={czMaxPriceProduct.productCompliance} />
					<Checkbox
						disabled={disableSelection}
						focused={lastSelectedProduct}
						checked={!disableSelection && values.selected}
						id={`product-selection-${product.id}`}
						onChange={this.selectionChanged}
					/>
				</div>
				<div className={styles.usedForCalculationCell}>
					<Link styles={{ button: styles.usedForCalculationCellContent }} tabIndex={-1} to={detailLinkString}>
						{lowestPriceIndex !== null ? (
							<Icon name='formula' title={`${this.renderLowerPriceIndex(lowestPriceIndex)} price`} />
						) : null}
					</Link>
				</div>
			</div>
		);
	}

	renderReferencePriceSelect(prompt: ReactNode) {
		const { czMaxPriceProduct, values, onChange } = this.props;

		return (
			<SegmentedSelect
				isValid={values.referencePrice.isValid}
				prompt={prompt}
				selectedOption={values.referencePrice.value}
				styles={defaultSegmentedSelectStyles}
				onSelect={(value) =>
					onChange(czMaxPriceProduct, value, values.packageSize.value, values.packageSizeForSingleUnit.value)
				}
			>
				<SegmentedSelectPage>
					<SegmentedSelectSegment>
						{czMaxPriceProduct.referencePrices.map((price) => (
							<SegmentedSelectOption
								key={price.calculatedPrice.type}
								prompt={this.renderReferencePricePrompt(price)}
								value={price.calculatedPrice.type}
							>
								<div>{price.calculatedPrice.description}</div>
								<div>{price.calculatedPrice.price.roundedHumanFormat()}</div>
								<div>
									<strong>{price.sourcePrice.price?.roundedHumanFormat()}</strong>
								</div>
							</SegmentedSelectOption>
						))}
					</SegmentedSelectSegment>
				</SegmentedSelectPage>
			</SegmentedSelect>
		);
	}

	renderReferencePricePrompt(price: ReferencePrice): ReactNode {
		return (
			<abbr data-testid='cell-reference-price' title={price.calculatedPrice.description}>
				<div>{price.calculatedPrice.price.roundedHumanFormat()}</div>
				<div>
					<strong>{price.sourcePrice.price?.roundedHumanFormat()}</strong>
				</div>
			</abbr>
		);
	}

	renderPackagePrice(referencePrice: ReferencePrice | null): ReactNode {
		if (referencePrice === null) {
			return null;
		}

		const {
			values: { packageSize },
			referencePackageSize,
		} = this.props;
		if (
			packageSize.isValid === false ||
			(packageSize.isValid === null && packageSize.value !== '') ||
			referencePackageSize === null
		) {
			return null;
		}

		const parsedPackageSize = packageSize.value !== '' ? parseHumanFloat(packageSize.value) : referencePackageSize;
		if (parsedPackageSize === null) {
			return null;
		}

		const { calculatedPrice, sourcePrice } = referencePrice;

		return (
			<div data-testid='cell-calc-price'>
				<div>
					{calculateProductPackagePrice(
						calculatedPrice.price,
						parsedPackageSize,
						referencePackageSize
					).roundedHumanFormat()}
				</div>
				<div>
					<strong>
						{sourcePrice.price !== null
							? calculateProductPackagePrice(
									sourcePrice.price,
									parsedPackageSize,
									referencePackageSize
							  ).roundedHumanFormat()
							: null}
					</strong>
				</div>
			</div>
		);
	}

	selectionChanged = (checked: boolean, event: React.MouseEvent<HTMLLabelElement, MouseEvent>) => {
		const { czMaxPriceProduct, onSelect } = this.props;
		onSelect(czMaxPriceProduct, checked, event.nativeEvent.shiftKey);
	};

	private renderLowerPriceIndex(index: number) {
		switch (index) {
			case 0:
				return 'Lowest';
			case 1:
				return '2nd lowest';
			case 2:
				return '3rd lowest';
			default:
				return `${index}. lowest`;
		}
	}
}
